import React, { useState } from 'react';
import { getMatchupLogos } from '../utils/teamLogos';

/**
 * TeamLogo component with fallback handling
 */
const TeamLogo = ({ src, alt, className = "" }) => {
  const [hasError, setHasError] = useState(false);
  
  if (!src || hasError) {
    return null;
  }
  
  return (
    <img
      src={src}
      alt={alt}
      className={`object-contain ${className}`}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};

/**
 * TeamMatchup component - displays matchup with team logos
 * @param {string} matchup - The matchup string (e.g., "Cavaliers @ Heat")
 * @param {string} sport - The sport (NBA, MLB, NHL, etc.)
 * @param {string} className - Additional CSS classes
 * @param {boolean} compact - Use compact layout for smaller cards
 */
const TeamMatchup = ({ matchup, sport, className = "", compact = false }) => {
  const { team1, team2, team1Logo, team2Logo } = getMatchupLogos(matchup, sport);
  
  // Determine separator from original matchup
  const separator = matchup?.includes(' @ ') ? '@' : 'vs';
  
  // Check if we have logos for this sport (NBA, MLB, NHL only)
  const showLogos = ['NBA', 'MLB', 'NHL'].includes(sport?.toUpperCase());
  const hasAnyLogo = team1Logo || team2Logo;
  
  // If no logos available, just show the matchup text
  if (!showLogos || !hasAnyLogo || !team1 || !team2) {
    return (
      <span className={`font-heading text-xl md:text-2xl font-bold text-white uppercase tracking-tight ${className}`}>
        {matchup}
      </span>
    );
  }
  
  // Compact layout for smaller displays
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {team1Logo && (
          <TeamLogo 
            src={team1Logo} 
            alt={team1}
            className="w-6 h-6 flex-shrink-0"
          />
        )}
        <span className="font-heading text-lg font-bold text-white uppercase tracking-tight">
          {team1}
        </span>
        <span className="text-zinc-500 text-sm font-medium">{separator}</span>
        {team2Logo && (
          <TeamLogo 
            src={team2Logo} 
            alt={team2}
            className="w-6 h-6 flex-shrink-0"
          />
        )}
        <span className="font-heading text-lg font-bold text-white uppercase tracking-tight">
          {team2}
        </span>
      </div>
    );
  }
  
  // Standard layout with logos
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        {team1Logo && (
          <TeamLogo 
            src={team1Logo} 
            alt={team1}
            className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0"
          />
        )}
        <span className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-tight">
          {team1}
        </span>
      </div>
      
      <span className="text-zinc-500 text-base font-medium px-1">{separator}</span>
      
      <div className="flex items-center gap-2">
        {team2Logo && (
          <TeamLogo 
            src={team2Logo} 
            alt={team2}
            className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0"
          />
        )}
        <span className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-tight">
          {team2}
        </span>
      </div>
    </div>
  );
};

export default TeamMatchup;
export { TeamLogo };
